import React, { useState } from 'react';
import type { Event } from '../../types';
import { api } from '../../services/api';
import { useLang } from '../../i18n/context';

interface EditEventModalProps {
  event: Event;
  onClose: () => void;
  onUpdated: () => void;
}

const GENRE_OPTIONS = ['dub', 'reggae', 'roots', 'dancehall', 'sound system', 'steppers', 'ska', 'rocksteady'];
const STATUSES: Event['status'][] = ['validated', 'pending', 'rejected'];

/** Admin editor for an existing event. Wired to PUT /events/:id, which resolves
 *  the free-text venue/city + artists the same way the Add/OCR paths do. */
export function EditEventModal({ event, onClose, onUpdated }: EditEventModalProps) {
  const { t } = useLang();
  const [form, setForm] = useState({
    title: event.title || '',
    date: event.date || '',
    venue_name: event.venue_name || '',
    venue_city: event.city || event.venue_city || '',
    start_time: event.start_time || '',
    end_time: event.end_time || '',
    price_info: event.price_info || '',
    is_free: !!event.is_free,
    status: event.status,
    artists: (event.artists || []).map(a => a.name).join(', '),
    genre_tags: [...(event.genre_tags || [])],
    description: event.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }));
  const toggleGenre = (g: string) =>
    setForm(p => ({ ...p, genre_tags: p.genre_tags.includes(g) ? p.genre_tags.filter(x => x !== g) : [...p.genre_tags, g] }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title || !form.date) { setError(t('edit_event.missing')); return; }
    setError('');
    setSaving(true);
    try {
      await api.updateEvent(event.id, {
        title: form.title,
        date: form.date,
        venue_name: form.venue_name || null,
        venue_city: form.venue_city || null,
        city: form.venue_city || null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        price_info: form.price_info || null,
        is_free: form.is_free,
        status: form.status,
        artists: form.artists,
        genre_tags: form.genre_tags,
        description: form.description || null,
      });
      onUpdated();
      onClose();
    } catch (err: any) {
      const msg = String(err?.message || '');
      setError(/auth|401|expir/i.test(msg) ? t('flyer.session_expired') : (msg || t('flyer.save_failed')));
      console.error('Failed to update event:', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{t('edit_event.title')}</div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('add_event.title_label')} *</label>
            <input className="form-input" required value={form.title} onChange={e => set('title', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">{t('add_event.date_label')} *</label>
              <input className="form-input" type="date" required value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('edit_event.status_label')}</label>
              <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('add_event.venue_label')}</label>
              <input className="form-input" value={form.venue_name} onChange={e => set('venue_name', e.target.value)} placeholder={t('add_event.venue_placeholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('add_event.city_label')}</label>
              <input className="form-input" value={form.venue_city} onChange={e => set('venue_city', e.target.value)} placeholder={t('add_event.city_placeholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('flyer.start')}</label>
              <input className="form-input" type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('flyer.end')}</label>
              <input className="form-input" type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('add_event.price_label')}</label>
              <input className="form-input" value={form.price_info} onChange={e => set('price_info', e.target.value)} placeholder={t('add_event.price_placeholder')} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: form.is_free ? '#4caf50' : 'var(--text-secondary)' }}>
                <input type="checkbox" checked={form.is_free} onChange={e => set('is_free', e.target.checked)} /> {t('edit_event.free')}
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('flyer.artists')}</label>
            <input className="form-input" value={form.artists} onChange={e => set('artists', e.target.value)} placeholder="Artist 1, Artist 2" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('add_event.genres_label')}</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {GENRE_OPTIONS.map(g => (
                <button key={g} type="button" className={`filter-chip ${form.genre_tags.includes(g) ? 'active' : ''}`} onClick={() => toggleGenre(g)}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('add_event.description_label')}</label>
            <textarea className="form-input" rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          {error && (
            <div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '4px', background: 'rgba(229,57,53,0.12)', border: '1px solid var(--red)', fontSize: '12px', color: 'var(--red)', textAlign: 'center' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-success" disabled={saving || !form.title || !form.date}>
              {saving ? t('flyer.saving') : t('edit_event.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
